/**
 * FO-425 (batch-58) §B4 - pure layout-helpers voor een planbord-weergave.
 *
 * Het klantenportaal krijgt een eigen bord-weergave (geen 1:1 hergebruik van de
 * backoffice-componenten), maar de REKENKUNDE achter het bord hoort één bron te
 * hebben: tijd-naar-positie en de groepering per dag. Die staan daarom hier,
 * zodat klant-view en interne view niet uiteen kunnen lopen.
 *
 * Alles puur: geen DOM, geen `Date`-tijdzone-afhankelijkheid, geen I/O. Tijden
 * zijn HH:mm-strings zoals ze op `WerkbonDayEntry` staan; datums zijn
 * `yyyy-MM-dd`-sleutels.
 */
import { parseHHmmToMinutes } from './werkbon-hours'
import { isNachtdienst } from './planning-dienst'

/** Standaard-dagvenster van het bord (06:00 - 22:00) in minuten na middernacht. */
export const BORD_VENSTER_START_MIN = 6 * 60
export const BORD_VENSTER_EIND_MIN = 22 * 60

/** Een tijdvenster waarin blokken gepositioneerd worden, in minuten na middernacht. */
export interface BordVenster {
  startMin: number
  eindMin: number
}

/** Minimale vorm van een dienst voor de layout-berekening. */
export interface BordDienstTijden {
  /** Begintijd HH:mm. */
  startTijd: string
  /** Eindtijd HH:mm. Mag vóór `startTijd` liggen (nachtdienst). */
  eindTijd: string
}

/**
 * Start- en eindminuut van een dienst binnen de kalenderdag waarop hij begint.
 * Een nachtdienst (eind < start) loopt door voorbij 1440; dat is bewust, zodat
 * een blok visueel doorloopt tot de onderrand in plaats van om te klappen.
 *
 * Geeft `null` bij ongeldige HH:mm-invoer - de aanroeper rendert dan geen blok
 * op tijd-positie maar valt terug op de lijstweergave.
 */
export function dienstMinuten(dienst: BordDienstTijden): { startMin: number; eindMin: number } | null {
  const startMin = parseHHmmToMinutes(dienst.startTijd)
  const eindRuw = parseHHmmToMinutes(dienst.eindTijd)
  if (startMin == null || eindRuw == null) return null
  const eindMin = isNachtdienst(dienst.startTijd, dienst.eindTijd) ? eindRuw + 1440 : eindRuw
  return { startMin, eindMin }
}

/**
 * Het venster dat alle diensten omvat, met `BORD_VENSTER_*` als ondergrens en
 * afronding op hele uren. Zo houdt een bord met alleen dagdiensten de vertrouwde
 * 06:00-22:00-schaal, terwijl een nacht- of vroege dienst het venster oprekt in
 * plaats van buiten beeld te vallen.
 *
 * Lege invoer geeft het standaardvenster terug.
 */
export function bepaalBordVenster(diensten: readonly BordDienstTijden[]): BordVenster {
  let startMin = BORD_VENSTER_START_MIN
  let eindMin = BORD_VENSTER_EIND_MIN
  for (const d of diensten) {
    const m = dienstMinuten(d)
    if (!m) continue
    if (m.startMin < startMin) startMin = Math.floor(m.startMin / 60) * 60
    if (m.eindMin > eindMin) eindMin = Math.ceil(m.eindMin / 60) * 60
  }
  // Guard: nooit een leeg of omgekeerd venster teruggeven (deling door 0).
  if (eindMin <= startMin) eindMin = startMin + 60
  return { startMin, eindMin }
}

/** Positie van een blok binnen het venster, als percentage van de venster-hoogte. */
export interface BordBlokPositie {
  /** Afstand van de bovenrand, 0-100. */
  topPct: number
  /** Hoogte, > 0 en zodanig dat top + hoogte <= 100. */
  hoogtePct: number
}

/** Minimale blok-hoogte in procenten, zodat een korte oproep leesbaar blijft. */
const MIN_HOOGTE_PCT = 4

/**
 * Zet een dienst om naar een positie binnen `venster`. Klemt op de vensterranden
 * (een dienst die vóór het venster begint of erna eindigt wordt afgekapt, nooit
 * buiten het bord getekend) en houdt een leesbare minimumhoogte aan.
 *
 * Geeft `null` bij ongeldige tijden of wanneer de dienst volledig buiten het
 * venster valt.
 */
export function bordBlokPositie(
  dienst: BordDienstTijden,
  venster: BordVenster,
): BordBlokPositie | null {
  const m = dienstMinuten(dienst)
  if (!m) return null
  const totaal = venster.eindMin - venster.startMin
  if (totaal <= 0) return null
  const start = Math.max(m.startMin, venster.startMin)
  const eind = Math.min(m.eindMin, venster.eindMin)
  if (eind <= venster.startMin || start >= venster.eindMin) return null

  const topPct = ((start - venster.startMin) / totaal) * 100
  const ruweHoogte = ((eind - start) / totaal) * 100
  const hoogtePct = Math.max(MIN_HOOGTE_PCT, ruweHoogte)
  // Nooit voorbij de onderrand: schuif desnoods het blok omhoog.
  const top = Math.min(topPct, Math.max(0, 100 - hoogtePct))
  return { topPct: top, hoogtePct }
}

/** De uur-markeringen van het venster (voor de tijd-as links van het bord). */
export function bordUurMarkeringen(venster: BordVenster): number[] {
  const uren: number[] = []
  const eersteUur = Math.ceil(venster.startMin / 60)
  const laatsteUur = Math.floor(venster.eindMin / 60)
  for (let u = eersteUur; u <= laatsteUur; u++) uren.push(u)
  return uren
}

/** Minimale vorm van een dagregel voor de groepering. */
export interface BordDagregel extends BordDienstTijden {
  /** Kalenderdag `yyyy-MM-dd` waarop de dienst begint. */
  datum: string
}

export interface BordDag<T extends BordDagregel> {
  /** Kalenderdag `yyyy-MM-dd`. */
  datum: string
  /** De diensten van die dag, gesorteerd op begintijd en daarna eindtijd. */
  items: T[]
}

/**
 * Groepeert dagregels per kalenderdag over een expliciete reeks dagen.
 *
 * De dagen komen van de aanroeper (de week-afbakening), niet uit de data: een
 * dag zonder diensten hoort als lege kolom op het bord te blijven staan. Regels
 * op een datum buiten `dagen` vallen weg - dat kan alleen bij een aanroep-fout,
 * en stil een achtste kolom bijtekenen is erger dan hem niet tonen.
 *
 * Binnen een dag wordt gesorteerd op begintijd, dan eindtijd (stabiel voor
 * gelijke tijden: de invoervolgorde blijft behouden).
 */
export function groepeerPerDag<T extends BordDagregel>(
  dagen: readonly string[],
  regels: readonly T[],
): Array<BordDag<T>> {
  const perDag = new Map<string, T[]>()
  for (const dag of dagen) perDag.set(dag, [])
  for (const regel of regels) {
    const lijst = perDag.get(regel.datum)
    if (!lijst) continue
    lijst.push(regel)
  }
  return dagen.map((datum) => ({
    datum,
    items: (perDag.get(datum) ?? []).slice().sort(vergelijkOpTijd),
  }))
}

function vergelijkOpTijd(a: BordDienstTijden, b: BordDienstTijden): number {
  const ma = dienstMinuten(a)
  const mb = dienstMinuten(b)
  // Ongeldige tijden achteraan i.p.v. de sortering laten omvallen.
  if (!ma && !mb) return 0
  if (!ma) return 1
  if (!mb) return -1
  if (ma.startMin !== mb.startMin) return ma.startMin - mb.startMin
  return ma.eindMin - mb.eindMin
}

/**
 * De uren-opbouw van een dienst in leesbare delen (126 P2): bruto span, pauze en
 * netto uren. Maakt "wisselende uren bij gelijke diensten" verklaarbaar - het
 * verschil zit vrijwel altijd in de pauze.
 *
 * `nettoUren` komt bewust NIET uit deze berekening in de payload: daar staat de
 * opgeslagen `hours`. Deze functie levert de opbouw ter TOELICHTING; wijkt de
 * som af (bv. handmatig gecorrigeerde uren), dan blijft de opgeslagen waarde
 * leidend en toont de UI de opbouw ernaast.
 */
export interface UrenOpbouw {
  /** Bruto duur in minuten (eind - start, nachtdienst meegerekend). */
  brutoMinuten: number
  /** Pauze in minuten. */
  pauzeMinuten: number
  /** Bruto - pauze, nooit negatief. */
  nettoMinuten: number
}

export function berekenUrenOpbouw(
  dienst: BordDienstTijden,
  pauzeMinuten: number,
): UrenOpbouw | null {
  const m = dienstMinuten(dienst)
  if (!m) return null
  const brutoMinuten = m.eindMin - m.startMin
  const pauze = Math.max(0, pauzeMinuten)
  return {
    brutoMinuten,
    pauzeMinuten: pauze,
    nettoMinuten: Math.max(0, brutoMinuten - pauze),
  }
}

/** Kolom-toewijzing van een blok binnen een dag met overlappende diensten. */
export interface BordKolomPlaatsing {
  /** 0-gebaseerde kolom binnen de dag. */
  kolom: number
  /** Totaal aantal kolommen dat de dag nodig heeft (>= 1). */
  kolommen: number
}

/**
 * Verdeelt de diensten van een dag over kolommen zodat overlappende blokken
 * naast elkaar staan in plaats van elkaar af te dekken.
 *
 * Greedy interval-kleuring op begintijd: een dienst pakt de laagste kolom die op
 * dat moment vrij is. Niet-overlappende diensten delen dus gewoon kolom 0 en een
 * dag met drie ploegen achter elkaar blijft één brede kolom - alleen echte
 * overlap kost breedte. Diensten met ongeldige tijden krijgen kolom 0; ze worden
 * elders sowieso zonder tijd-positie gerenderd.
 *
 * De uitvoer heeft dezelfde lengte en volgorde als de invoer, zodat de aanroeper
 * per index kan opzoeken.
 */
export function verdeelInKolommen(
  diensten: readonly BordDienstTijden[],
): BordKolomPlaatsing[] {
  // Sorteer-index op begintijd, maar geef de plaatsing terug in invoervolgorde.
  const volgorde = diensten
    .map((d, index) => ({ index, minuten: dienstMinuten(d) }))
    .sort((a, b) => {
      if (!a.minuten && !b.minuten) return a.index - b.index
      if (!a.minuten) return 1
      if (!b.minuten) return -1
      if (a.minuten.startMin !== b.minuten.startMin) return a.minuten.startMin - b.minuten.startMin
      return a.minuten.eindMin - b.minuten.eindMin
    })

  const kolomEindes: number[] = []
  const plaatsing: number[] = new Array<number>(diensten.length).fill(0)
  for (const { index, minuten } of volgorde) {
    if (!minuten) continue
    let kolom = kolomEindes.findIndex((eind) => eind <= minuten.startMin)
    if (kolom === -1) {
      kolom = kolomEindes.length
      kolomEindes.push(minuten.eindMin)
    } else {
      kolomEindes[kolom] = minuten.eindMin
    }
    plaatsing[index] = kolom
  }

  const kolommen = Math.max(1, kolomEindes.length)
  return plaatsing.map((kolom) => ({ kolom, kolommen }))
}
